import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileText,
  Paperclip,
  Send,
  CheckCircle2,
  Lock,
  Clock,
  Wallet,
  BadgeCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ABSTRACT_CATEGORIES,
  wordCount,
} from "@/lib/mockStore";
import { abstractsApi, readError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const MAX_WORDS = 500;
const SUCCESS_MSG =
  "Abstract Submission successful. You will be contacted by the Organising Committee shortly.";

const emptyForm = {
  title: "",
  category: "case-report",
  authors: "",
  presentingAuthor: "",
  correspondingEmail: "",
  body: "",
};

const AbstractSubmissions = () => {
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [pendingFile, setPendingFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await abstractsApi.eligibility();
      setEligibility(data);
    } catch (err) {
      // Auth guard on parent route already prevents anonymous access.
      setEligibility(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (eligibility?.eligible) {
      setForm((f) => ({
        ...f,
        presentingAuthor: f.presentingAuthor || user?.name || "",
        correspondingEmail: f.correspondingEmail || user?.email || "",
      }));
    }
  }, [eligibility?.eligible, user?.name, user?.email]);

  const words = wordCount(form.body);
  const overLimit = words > MAX_WORDS;

  const isPg = user?.packageId === "pg";
  const isVerified = user?.paymentStatus === "verified";

  // Non-PG delegates never see this section — enforced by the parent Dashboard
  // component. This defensive early-return keeps the component pure for tests.
  if (!isPg) return null;
  if (loading) {
    return (
      <div data-testid="dashboard-abstracts-section">
        <div className="animate-pulse text-sm text-gray-400">Loading abstract submission…</div>
      </div>
    );
  }

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5 MB.");
      return;
    }
    setPendingFile(file);
    toast.success(`Attached: ${file.name}`);
  };

  const submit = async () => {
    if (!eligibility?.eligible) return;
    if (!form.title.trim()) return toast.error("Please give the abstract a title.");
    if (!form.authors.trim()) return toast.error("List at least one author.");
    if (!form.body.trim() || words < 50)
      return toast.error("Abstract body must be at least 50 words.");
    if (overLimit) return toast.error(`Abstract exceeds ${MAX_WORDS} words.`);
    setSubmitting(true);
    try {
      const saved = await abstractsApi.create({
        title: form.title,
        category: form.category,
        authors: form.authors,
        presentingAuthor: form.presentingAuthor,
        correspondingEmail: form.correspondingEmail,
        body: form.body,
        status: "submitted",
      });
      if (pendingFile) {
        try {
          await abstractsApi.uploadFile(saved.id, pendingFile);
        } catch (err) {
          // File attach failure is non-fatal — the submission itself succeeded.
          toast.error(readError(err) || "Attachment upload failed.");
        }
      }
      toast.success(SUCCESS_MSG);
      await reload();
    } catch (err) {
      toast.error(readError(err) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // Payment not verified — nudge the user to complete payment.
  if (!isVerified) {
    return (
      <div data-testid="dashboard-abstracts-section">
        <SectionHeader />
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-5"
          data-testid="abstract-need-payment"
        >
          <div className="flex items-start gap-3 text-sm text-amber-900">
            <Wallet className="mt-0.5 h-4 w-4" />
            <div>
              <div className="font-semibold">Complete your PG payment to unlock abstract submission.</div>
              <div className="mt-1 text-xs text-amber-800/80">
                ₹1,770 · Post Graduate Student · verified via Razorpay.
              </div>
            </div>
          </div>
          <Link to="/payment?package=pg" data-testid="abstract-goto-payment">
            <Button className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]">
              Continue to payment
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Already submitted — permanent locked view.
  if (eligibility?.alreadySubmitted) {
    const a = eligibility.submittedAbstract;
    return (
      <div data-testid="dashboard-abstracts-section">
        <SectionHeader />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50 p-6"
          data-testid="abstract-submitted-card"
        >
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-800">
            <BadgeCheck className="h-4 w-4" /> Abstract Submission
          </div>
          <div
            className="mt-2 font-display text-2xl font-semibold text-emerald-900"
            data-testid="abstract-submitted-status"
          >
            Successfully Submitted
          </div>
          <p className="mt-3 max-w-2xl text-sm text-emerald-900/80">
            Thank you — the Organising Committee has received your abstract and
            will contact you shortly. Only one submission per PG delegate is
            allowed.
          </p>
          {a && (
            <div className="mt-4 rounded-2xl bg-white/70 p-4 text-sm text-emerald-900 ring-1 ring-emerald-100">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                Your submission
              </div>
              <div className="mt-1 font-display text-base font-semibold">{a.title}</div>
              <div className="mt-1 text-xs text-emerald-800/80">
                Category: {ABSTRACT_CATEGORIES.find((c) => c.id === a.category)?.label || a.category} · Submitted {new Date(a.updatedAt || a.createdAt).toLocaleString()}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // Deadline passed and no existing submission.
  if (eligibility?.deadlinePassed) {
    return (
      <div data-testid="dashboard-abstracts-section">
        <SectionHeader />
        <div
          className="mt-4 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-900"
          data-testid="abstract-deadline-closed"
        >
          <Clock className="mt-0.5 h-4 w-4" />
          <div>
            <div className="font-semibold">Abstract submissions are closed.</div>
            <div className="mt-1 text-xs">
              The deadline (30 November 2026 · 11:59 PM IST) has passed. New submissions are no longer accepted.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Eligible → single-shot submission form.
  return (
    <div data-testid="dashboard-abstracts-section">
      <SectionHeader />
      <div
        className="mt-4 rounded-3xl border border-apicon-navy/10 bg-white p-6 shadow-sm"
        data-testid="abstract-submit-form"
      >
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-apicon-green">
          <Lock className="h-3 w-3" /> PG Delegate · One submission allowed
          <span className="mx-2 h-1 w-1 rounded-full bg-gray-300" />
          Deadline · 30 Nov 2026, 11:59 PM IST
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="abs-title" className="text-sm text-apicon-navy">
              Abstract title
            </Label>
            <Input
              id="abs-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="A concise, descriptive title"
              className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
              data-testid="abstract-input-title"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-sm text-apicon-navy">Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
            >
              <SelectTrigger
                className="h-11 rounded-xl border-gray-200 focus:ring-apicon-navy"
                data-testid="abstract-select-category"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ABSTRACT_CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="abs-presenting" className="text-sm text-apicon-navy">
              Presenting author
            </Label>
            <Input
              id="abs-presenting"
              value={form.presentingAuthor}
              onChange={(e) =>
                setForm((f) => ({ ...f, presentingAuthor: e.target.value }))
              }
              className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
              data-testid="abstract-input-presenting"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="abs-authors" className="text-sm text-apicon-navy">
              Author list
            </Label>
            <Input
              id="abs-authors"
              value={form.authors}
              onChange={(e) =>
                setForm((f) => ({ ...f, authors: e.target.value }))
              }
              placeholder="Comma-separated, in order"
              className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
              data-testid="abstract-input-authors"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="abs-corresp" className="text-sm text-apicon-navy">
              Corresponding email
            </Label>
            <Input
              id="abs-corresp"
              type="email"
              value={form.correspondingEmail}
              onChange={(e) =>
                setForm((f) => ({ ...f, correspondingEmail: e.target.value }))
              }
              className="h-11 rounded-xl border-gray-200 focus-visible:ring-apicon-navy"
              data-testid="abstract-input-corresp"
            />
          </div>

          <div className="grid gap-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="abs-body" className="text-sm text-apicon-navy">
                Abstract body
              </Label>
              <span
                className={cn(
                  "text-xs font-medium",
                  overLimit
                    ? "text-red-600"
                    : words > MAX_WORDS * 0.8
                      ? "text-amber-700"
                      : "text-gray-500",
                )}
                data-testid="abstract-word-count"
              >
                {words} / {MAX_WORDS} words
              </span>
            </div>
            <Textarea
              id="abs-body"
              rows={9}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Background, methods, results, conclusion…"
              className={cn(
                "min-h-[220px] rounded-2xl border-gray-200 text-sm focus-visible:ring-apicon-navy",
                overLimit && "border-red-400 focus-visible:ring-red-500",
              )}
              data-testid="abstract-input-body"
            />
          </div>

          <div className="sm:col-span-2">
            <div className="rounded-2xl border border-dashed border-gray-200 p-4">
              <label
                htmlFor="abs-file"
                className="flex cursor-pointer items-center justify-between gap-3 text-sm"
                data-testid="abstract-file-label"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-apicon-mist text-apicon-navy">
                    <Paperclip className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-medium text-apicon-navy">
                      {pendingFile ? pendingFile.name : "Attach abstract PDF/DOCX (optional)"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {pendingFile
                        ? `${(pendingFile.size / 1024).toFixed(1)} KB`
                        : "PDF, DOC, DOCX up to 5 MB"}
                    </div>
                  </div>
                </div>
                <span className="rounded-full border border-apicon-navy/20 px-3 py-1 text-xs font-medium text-apicon-navy hover:bg-apicon-mist">
                  {pendingFile ? "Replace" : "Choose file"}
                </span>
              </label>
              <input
                id="abs-file"
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={onFile}
                data-testid="abstract-input-file"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
          <p className="text-xs text-gray-500">
            You can register today and submit any time before <b>30 Nov 2026, 11:59 PM IST</b>.
            One submission per PG delegate.
          </p>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]"
            data-testid="abstract-submit"
          >
            <Send className="mr-1 h-4 w-4" />
            {submitting ? "Submitting…" : "Submit abstract"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = () => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
      Abstract submission
    </div>
    <h2 className="mt-2 font-display text-2xl font-semibold text-apicon-navy">
      PG Academic Track · Assam APICON 2026
    </h2>
    <p className="mt-1 max-w-xl text-sm text-gray-600">
      Exclusive to Post Graduate Student delegates. Submit your case report,
      original research or systematic review to the Organising Committee.
    </p>
  </div>
);

export default AbstractSubmissions;
